// Slice.ts
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from 'config';

interface NodeData {
  depth?: number;
  layer?: string;
  label?: string;
  treatments?: any[]; 
  lambda?: number;
  k?: number;
  unitPrice?: number;
  weight?: string;
}

interface CustomNode {
  id: string;
  type: 'special' | 'default' | string;
  data: NodeData;
  
  position?: { x: number; y: number };
}

interface CustomEdge {
  id: string;
  source: string;
  target: string;
}

interface LastLayerTreatment {
  id: string;
  criteria: Record<string, string>;
  treatments: any[];
}

interface LastLayerDeterioration {
  id: string;
  criteria: Record<string, string>;
  lambda: number;
  k: number;
}

interface LastLayerValuation {
  id: string;
  criteria: Record<string, string>;
  unitPrice: number;
  weight: string;
}

interface InfoItem {
  asset: string;
  name: string;
}

interface TreeDetails<L> {
  info: InfoItem[];
  layers: string[];
  nodes: CustomNode[];
  edges: CustomEdge[];
  lastLayerDetails: L[];
}

interface TreatmentItem {
  name: string;
  treatment: TreeDetails<LastLayerTreatment>;
  _id?: string;
}

interface DeteriorationItem {
  name: string;
  details: TreeDetails<LastLayerDeterioration>;
  _id?: string;
}

interface ValuationItem {
  name: string;
  details: TreeDetails<LastLayerValuation>;
  _id?: string;
}

interface FunctionState {
  treeData: any[];
  nodesT: CustomNode[];
  edgesT: CustomEdge[];
  treatments: TreatmentItem[];
  disabledBtn: boolean;

  treeDataD: any[];
  nodesD: CustomNode[];
  edgesD: CustomEdge[];
  treatmentsD: DeteriorationItem[];
  disabledBtnD: boolean;

  treeDataV: any[];
  nodesV: CustomNode[];
  edgesV: CustomEdge[];
  valuations: ValuationItem[];
  disabledBtnV: boolean;

  itemData: any;
  selectedCategoryData?: any;
}

interface RootState {
  functionStore: FunctionState;
}

interface EditThunkArgs {
  data1: string[];
  selectedIdentity: string;
  selectedAttribute: string;
  EditKey: string;
  editingName: string;
  name: string;
}

const initialState: FunctionState = {
  treeData: [],
  nodesT: [],
  edgesT: [],
  treatments: [],
  disabledBtn: true,
  treeDataD: [],
  nodesD: [],
  edgesD: [],
  treatmentsD: [],
  disabledBtnD: true,
  treeDataV: [],
  nodesV: [],
  edgesV: [],
  valuations: [],
  disabledBtnV: true,
  itemData: {},
  selectedCategoryData: {}
};


export const handleEdit = createAsyncThunk<any, EditThunkArgs, { state: RootState }>(
  'functionStore/handleEdit',
  async ({ data1, selectedIdentity, selectedAttribute, EditKey, editingName, name }, { getState }) => {
    const state = getState().functionStore;
    const serviceToken = localStorage.getItem('serviceToken');

    const maxDepth = data1.length;

    const parentMap: Record<string, string> = {};
    state.edgesT.forEach((edge) => {
      parentMap[edge.target] = edge.source;
    });

    const lastLayerDetails: LastLayerTreatment[] = state.nodesT
      .filter((node) => node.type === 'special' && node.data.depth === maxDepth)
      .map((node) => {
        const criteria: Record<string, string> = {};
        let currentNode: CustomNode | undefined = state.nodesT.find((n) => n.id === parentMap[node.id]);

        while (currentNode && currentNode.data.layer && currentNode.data.layer !== 'Root') {
          criteria[currentNode.data.layer] = currentNode.data.label || '';

          const parentId = parentMap[currentNode.id];
          currentNode = state.nodesT.find((n) => n.id === parentId);
        }

        return {
          id: node.id,
          criteria: criteria,
          treatments: node.data.treatments || [] 
        };
      });

    const data = {
      newTreatment: {
        info: [
          {
            asset: `${selectedIdentity},${selectedAttribute}`,
            name: name
          }
        ],
        layers: data1,
        nodes: state.nodesT,
        edges: state.edgesT,
        lastLayerDetails: lastLayerDetails
      } as TreeDetails<LastLayerTreatment>,
      treatmentId: EditKey,
      access_to_all: true,
      type: `${selectedIdentity},${selectedAttribute}`,
      name: editingName === '' ? name : editingName
    };

    console.log('Data:', data);

    const response = await axios.put(`${BASE_URL}/treatment/edit-treatment`, data, {
      headers: {
        Authorization: `Bearer ${serviceToken}`
      }
    });

    return response.data;
  }
);

export const handleEditD = createAsyncThunk<any, EditThunkArgs, { state: RootState }>(
  'functionStore/handleEditD', 
  async ({ data1, selectedIdentity, selectedAttribute, EditKey, editingName, name }, { getState }) => {
    const state = getState().functionStore;
    const serviceToken = localStorage.getItem('serviceToken');

    const maxDepth = data1.length;

    const parentMap: Record<string, string> = {};
    state.edgesD.forEach((edge) => {
      parentMap[edge.target] = edge.source;
    });

    const lastLayerDetails: LastLayerDeterioration[] = state.nodesD
      .filter((node) => node.type === 'special' && node.data.depth === maxDepth)
      .map((node) => {
        const criteria: Record<string, string> = {};
        let currentNode: CustomNode | undefined = state.nodesD.find((n) => n.id === parentMap[node.id]); 

        while (currentNode && currentNode.data.layer && currentNode.data.layer !== 'Root') {
          criteria[currentNode.data.layer] = currentNode.data.label || '';

          const parentId = parentMap[currentNode.id];
          currentNode = state.nodesD.find((n) => n.id === parentId);
        }

        return {
          id: node.id,
          criteria: criteria,
          lambda: node.data.lambda || 0, 
          k: node.data.k || 0 
        };
      });

    const data = {
      newDetails: {
        info: [
          {
            asset: `${selectedIdentity},${selectedAttribute}`,
            name: name
          }
        ],
        layers: data1,
        nodes: state.nodesD,
        edges: state.edgesD,
        lastLayerDetails: lastLayerDetails
      } as TreeDetails<LastLayerDeterioration>,
      deteriorationId: EditKey,
      access_to_all: true,
      type: `${selectedIdentity},${selectedAttribute}`,
      name: editingName === '' ? name : editingName
    };

    console.log('Data:', data);

    const response = await axios.put(`${BASE_URL}/deterioration/edit-deterioration`, data, {
      headers: {
        Authorization: `Bearer ${serviceToken}`
      }
    });

    return response.data;
  }
);


const functionSlice = createSlice({
  name: 'functionStore',
  initialState,
  reducers: {
    handleSaveData: (state, action: PayloadAction<{ data: string[]; name3: string }>) => {
      const { data, name3 } = action.payload;
      const updatedTreatments = state.treatments.map((treatment) => {
        if (treatment.name === name3) {
          return {
            ...treatment,
            treatment: {
              ...treatment.treatment,
              layers: [...data]
            }
          };
        }
        return treatment;
      });
      state.treatments = updatedTreatments;
    },

    handelClickNodes: (state, action: PayloadAction<{ nodes: CustomNode[]; edges: CustomEdge[] }>) => {
      const { nodes, edges } = action.payload;
      state.nodesT = nodes;
      state.edgesT = edges;
    },
    buildTree: (state, action: PayloadAction<any[]>) => {
      state.treeData = action.payload;
      state.disabledBtn = false;
    },
    setTreatments: (state, action: PayloadAction<TreatmentItem[]>) => {
      state.treatments = action.payload;
    },
    handleSaveDataD: (state, action: PayloadAction<{ data: string[]; name3: string }>) => {
      const { data, name3 } = action.payload;
      const updatedTreatments = state.treatmentsD.map((treatment) => {
        if (treatment.name === name3) {
          return {
            ...treatment,
            details: {
              ...treatment.details,
              layers: [...data]
            }
          };
        }
        return treatment;
      });
      state.treatmentsD = updatedTreatments;
    },

    handelClickNodesD: (state, action: PayloadAction<{ nodes: CustomNode[]; edges: CustomEdge[] }>) => {
      const { nodes, edges } = action.payload;
      state.nodesD = nodes;
      state.edgesD = edges;
    },
    buildTreeD: (state, action: PayloadAction<any[]>) => {
      state.treeDataD = action.payload;
      state.disabledBtnD = false;
    },
    setTreatmentsD: (state, action: PayloadAction<DeteriorationItem[]>) => {
      state.treatmentsD = action.payload;
    },
    handleSaveDataV: (state, action: PayloadAction<{ data: string[]; name3: string }>) => {
      const { data, name3 } = action.payload;
      const updated = state.valuations.map((item) => {
        if (item.name === name3) {
          return {
            ...item,
            details: { ...item.details, layers: [...data] }
          };
        }
        return item;
      });
      state.valuations = updated;
    },
    updateValuationV: (state, action: PayloadAction<EditThunkArgs>) => {
      const { data1, selectedIdentity, selectedAttribute, EditKey, editingName, name } = action.payload;
      const maxDepth = data1.length;
      const parentMap: Record<string, string> = {};
      state.edgesV.forEach((edge) => {
        parentMap[edge.target] = edge.source;
      });

      const lastLayerDetails: LastLayerValuation[] = state.nodesV
        .filter((node) => node.type === 'special' && node.data.depth === maxDepth)
        .map((node) => {
          const criteria: Record<string, string> = {};
          let currentNode: CustomNode | undefined = state.nodesV.find((n) => n.id === parentMap[node.id]);

          while (currentNode && currentNode.data.layer && currentNode.data.layer !== 'Root') {
            criteria[currentNode.data.layer] = currentNode.data.label || '';
            const parentId = parentMap[currentNode.id];
            currentNode = state.nodesV.find((n) => n.id === parentId);
          }
          return {
            id: node.id,
            criteria,
            unitPrice: node.data.unitPrice || 0,
            weight: node.data.weight || ''
          };
        });

      state.valuations = state.valuations.map((v) => {
        if (v._id === EditKey) {
          return {
            ...v,
            name: editingName === '' ? name : editingName,
            type: `${selectedIdentity},${selectedAttribute}` as any, 
            details: {
              ...v.details,
              layers: data1,
              nodes: state.nodesV,
              edges: state.edgesV,
              lastLayerDetails: lastLayerDetails as TreeDetails<LastLayerValuation>['lastLayerDetails'] 
            }
          };
        }
        return v;
      });
    },
    handelClickNodesV: (state, action: PayloadAction<{ nodes: CustomNode[]; edges: CustomEdge[] }>) => {
      const { nodes, edges } = action.payload;
      state.nodesV = nodes;
      state.edgesV = edges;
    },
    buildTreeV: (state, action: PayloadAction<any[]>) => {
      state.treeDataV = action.payload;
      state.disabledBtnV = false;
    },
    setValuations: (state, action: PayloadAction<ValuationItem[]>) => {
      state.valuations = action.payload;
    },
    setItemData: (state, action: PayloadAction<any>) => {
      state.itemData = action.payload;
    },
    setSelectedCategoryData: (state, action: PayloadAction<any>) => {
      state.selectedCategoryData = action.payload;
    }
  }
});

export const {
  handleSaveData,
  setItemData,
  handelClickNodes,
  buildTree,
  setTreatments,
  handleSaveDataD,
  handelClickNodesD,
  buildTreeD,
  setTreatmentsD,
  handleSaveDataV,
  updateValuationV,
  handelClickNodesV,
  buildTreeV,
  setValuations,
  setSelectedCategoryData
} = functionSlice.actions;

export default functionSlice.reducer;